import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import '../components/copy-email-button';
import '../components/ui/ui-button';
import { navigate } from '../router';
import { boxSizingStyles, fullBleedStyles } from '../styles/lit-shared';

const CALENDAR_URL = 'https://calendar.app.google/3TMX3h7SGT1wkdp87';
const LINKEDIN_URL = 'https://www.linkedin.com/in/alejandro-serrano-notivoli/';
const SUBSTACK_URL = 'https://alejandrosn.substack.com';

const CONVICTIONS = [
  {
    label: 'Escucha',
    title: 'Primero entendemos el problema',
    description:
      'Llego preguntando, no con un paquete cerrado. Definir el alcance sirve para que sepas qué vas a tener, no para cortar el proyecto antes de empezar. Si hay que ajustar, se ajusta. Frenar siempre hay tiempo; limitarnos de entrada, no.',
  },
  {
    label: 'Oficio',
    title: 'La tecnología al servicio de lo que necesitas',
    description:
      'Webs, automatizaciones, paneles, integraciones: elijo las piezas que resuelven tu caso. Que vaya rápido y aguante no es un capricho técnico, es que quien lo use no se quede esperando. El resultado es tuyo; las herramientas, el medio.',
  },
  {
    label: 'Criterio',
    title: 'Te digo lo que pienso',
    description:
      'Si algo no encaja, lo hablo. No para cerrar la puerta, para que decidas con el mapa real. Prefiero una conversación incómoda al principio que un producto que no te sirve al final.',
  },
] as const;

const CAPABILITIES = [
  {
    title: 'Webs y páginas',
    description: 'Landings, marca, blog, reservas. Una página que se siente tuya y se entiende en el móvil.',
  },
  {
    title: 'Automatizaciones',
    description: 'Emails, CRM, facturas, avisos. Lo repetitivo deja de hacerse a mano.',
  },
  {
    title: 'Tiendas y cobros',
    description: 'Catálogo, pagos y pedidos. Vender sin montar un mamotreto.',
  },
  {
    title: 'Paneles y apps internas',
    description: 'Lo que tu equipo usa cada día: clientes, inventario, flujos, backoffice.',
  },
  {
    title: 'Conectar lo que ya tienes',
    description: 'Formularios, APIs, Google, la herramienta que ya pagas. Que hablen entre ellas.',
  },
  {
    title: 'Ponerlo en marcha',
    description: 'Infraestructura ligera y rápida. Lo lanzamos, lo medimos y lo podemos hacer crecer.',
  },
] as const;

@customElement('about-page')
export class AboutPage extends LitElement {
  private _revealObserver: IntersectionObserver | null = null;

  private goTo(path: string) {
    void navigate(path);
  }

  firstUpdated() {
    this._setupReveal();
  }

  disconnectedCallback() {
    this._revealObserver?.disconnect();
    this._revealObserver = null;
    super.disconnectedCallback();
  }

  private _setupReveal() {
    const root = this.shadowRoot;
    if (!root) return;

    const els = root.querySelectorAll('.reveal');
    if (!els.length) return;

    if (typeof IntersectionObserver === 'undefined') {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    this._revealObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            this._revealObserver?.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    );

    els.forEach((el) => this._revealObserver!.observe(el));
  }

  private renderChannels() {
    return html`
      <div class="actions">
        <ui-button href=${CALENDAR_URL} variant="primary">Reservar una llamada</ui-button>
        <copy-email-button></copy-email-button>
        <ui-button href=${LINKEDIN_URL} variant="secondary">LinkedIn</ui-button>
        <ui-button href=${SUBSTACK_URL} variant="secondary">Substack</ui-button>
      </div>
    `;
  }

  static styles = [
    boxSizingStyles,
    fullBleedStyles,
    css`
      :host {
        display: block;
        width: 100%;
      }

      /* —— Hero —— */
      .hero {
        margin-top: calc(-1 * var(--space-xl));
        margin-bottom: var(--space-md);
        padding: var(--space-2xl) var(--space-lg);
        overflow: hidden;
        background:
          radial-gradient(ellipse 70% 60% at 15% 40%, var(--glow-violet), transparent 55%),
          radial-gradient(ellipse 50% 50% at 85% 30%, var(--glow-cyan), transparent 50%),
          radial-gradient(ellipse 40% 100% at 0% 50%, var(--glow-violet), transparent 60%),
          radial-gradient(ellipse 40% 100% at 100% 50%, var(--glow-cyan), transparent 60%),
          var(--color-bg);
      }

      .hero-orb {
        position: absolute;
        border-radius: 50%;
        filter: blur(60px);
        pointer-events: none;
        animation: orb-drift 16s var(--ease-out) infinite alternate;
      }

      .hero-orb--1 {
        top: 10%;
        right: 5%;
        width: min(320px, 45vw);
        height: min(320px, 45vw);
        background: var(--glow-cyan);
      }

      .hero-orb--2 {
        bottom: -10%;
        left: 20%;
        width: min(260px, 40vw);
        height: min(260px, 40vw);
        background: var(--glow-fuchsia);
        animation-delay: -5s;
      }

      @keyframes orb-drift {
        from {
          transform: translate(0, 0) scale(1);
        }
        to {
          transform: translate(18px, -14px) scale(1.06);
        }
      }

      .hero-inner {
        position: relative;
        z-index: 1;
        display: grid;
        grid-template-columns: 1fr;
        align-items: center;
        gap: var(--space-xl);
        width: 100%;
        max-width: var(--max-width);
        margin: 0 auto;
      }

      .hero-portrait {
        order: -1;
        justify-self: center;
        width: min(210px, 56vw);
        margin: 0;
        aspect-ratio: 3 / 4;
        border-radius: var(--radius-lg);
        overflow: hidden;
        border: 1px solid var(--color-border-glow);
        background: var(--color-bg);
        box-shadow: var(--shadow-glow-primary), var(--shadow-lg);
      }

      .hero-portrait img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center 18%;
      }

      @media (min-width: 640px) and (max-width: 859px) {
        .hero-portrait {
          width: min(280px, 46vw);
        }
      }

      @media (min-width: 860px) {
        .hero-inner {
          grid-template-columns: minmax(0, 1.15fr) minmax(240px, 340px);
          gap: clamp(2rem, 4vw, 3.5rem);
        }

        .hero-portrait {
          order: 0;
          justify-self: end;
          width: 100%;
        }
      }

      .section-label {
        display: block;
        margin-bottom: var(--space-md);
        font-family: var(--font-mono);
        font-size: 0.6875rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.12em;
        color: var(--color-accent);
      }

      .hero-title {
        font-size: clamp(2rem, 5vw, 3rem);
        font-weight: 700;
        letter-spacing: -0.035em;
        line-height: 1.1;
        margin: 0 0 var(--space-sm);
        color: var(--color-text);
      }

      .hero-title .title-grad {
        background: var(--gradient-text);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .hero-tagline {
        font-family: var(--font-mono);
        font-size: 0.8125rem;
        font-weight: 500;
        letter-spacing: 0.04em;
        color: var(--color-primary-light);
        margin: 0 0 var(--space-md);
      }

      .hero-lead {
        font-size: 1.0625rem;
        color: var(--color-text-muted);
        line-height: 1.65;
        margin: 0 0 var(--space-xl);
        max-width: 54ch;
      }

      /* —— Sections —— */
      .section {
        padding: var(--space-2xl) var(--space-lg);
        max-width: var(--max-width);
        margin-inline: auto;
      }

      .section-title {
        font-size: clamp(1.75rem, 4vw, 2.5rem);
        font-weight: 700;
        letter-spacing: -0.03em;
        line-height: 1.15;
        margin: 0 0 var(--space-sm);
        color: var(--color-text);
      }

      .section-title .title-grad {
        background: var(--gradient-text);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .section-lead {
        color: var(--color-text-muted);
        font-size: 1.0625rem;
        line-height: 1.65;
        margin: 0 0 var(--space-xl);
        max-width: 58ch;
      }

      .reveal {
        opacity: 0;
        transform: translateY(18px);
        transition:
          opacity 700ms var(--ease-out),
          transform 700ms var(--ease-out);
      }

      .reveal.is-visible {
        opacity: 1;
        transform: translateY(0);
      }

      .reveal-delay-1 {
        transition-delay: 80ms;
      }
      .reveal-delay-2 {
        transition-delay: 160ms;
      }
      .reveal-delay-3 {
        transition-delay: 240ms;
      }
      .reveal-delay-4 {
        transition-delay: 320ms;
      }

      /* —— Story band —— */
      .band {
        padding: var(--space-2xl) var(--space-lg);
        overflow: hidden;
      }

      .band-inner {
        position: relative;
        z-index: 1;
        width: 100%;
        max-width: var(--max-width);
        margin: 0 auto;
      }

      .band--story {
        background:
          radial-gradient(ellipse 70% 80% at 0% 40%, var(--glow-violet), transparent 55%),
          radial-gradient(ellipse 45% 55% at 100% 70%, var(--glow-cyan), transparent 50%),
          radial-gradient(ellipse 40% 100% at 0% 50%, var(--glow-violet), transparent 60%),
          radial-gradient(ellipse 40% 100% at 100% 50%, var(--glow-cyan), transparent 60%),
          var(--color-bg);
      }

      .band--story .band-inner {
        padding-left: var(--space-xl);
        border-left: 2px solid transparent;
        border-image: linear-gradient(
            to bottom,
            var(--color-accent),
            var(--color-primary-light),
            transparent
          )
          1;
      }

      .story p {
        margin: 0 0 var(--space-md);
        color: var(--color-text-muted);
        font-size: 1.0625rem;
        line-height: 1.65;
        max-width: 62ch;
      }

      .story p:last-child {
        margin-bottom: 0;
      }

      /* —— Open grid —— */
      .open-grid {
        display: grid;
        gap: 0;
        grid-template-columns: 1fr;
        border-top: 1px solid var(--color-border);
      }

      @media (min-width: 860px) {
        .open-grid {
          grid-template-columns: repeat(3, 1fr);
        }
      }

      .open-item {
        padding: var(--space-lg) var(--space-md) var(--space-lg) 0;
        border-bottom: 1px solid var(--color-border);
      }

      @media (min-width: 860px) {
        .open-item {
          padding-right: var(--space-lg);
          border-right: 1px solid var(--color-border);
          padding-left: var(--space-lg);
        }

        .open-item:nth-child(3n) {
          padding-right: 0;
          border-right: none;
        }
      }

      .open-item .item-label {
        display: block;
        font-family: var(--font-mono);
        font-size: 0.6875rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: var(--color-accent);
        margin-bottom: var(--space-xs);
      }

      .open-item h3 {
        margin: 0 0 var(--space-xs);
        font-size: 1.125rem;
        font-weight: 600;
        letter-spacing: -0.02em;
        color: var(--color-text);
      }

      .open-item p {
        margin: 0;
        color: var(--color-text-muted);
        font-size: 0.9375rem;
        line-height: 1.6;
      }

      /* —— CTA band —— */
      .band--cta {
        margin-top: var(--space-md);
        margin-bottom: var(--space-xl);
        text-align: center;
        background:
          radial-gradient(ellipse 55% 65% at 35% 40%, var(--glow-violet), transparent 55%),
          radial-gradient(ellipse 45% 55% at 80% 60%, var(--glow-cyan), transparent 50%),
          radial-gradient(ellipse 40% 100% at 0% 50%, var(--glow-violet), transparent 60%),
          radial-gradient(ellipse 40% 100% at 100% 50%, var(--glow-cyan), transparent 60%),
          var(--color-bg);
      }

      .band--cta .section-title {
        margin-bottom: var(--space-md);
      }

      .band-lead {
        color: var(--color-text-muted);
        font-size: 1.0625rem;
        line-height: 1.65;
        margin: 0 auto var(--space-xl);
        max-width: 52ch;
      }

      .band-orb {
        position: absolute;
        border-radius: 50%;
        filter: blur(70px);
        pointer-events: none;
        animation: orb-drift 18s var(--ease-out) infinite alternate;
      }

      .band-orb--1 {
        top: -15%;
        right: 8%;
        width: min(280px, 40vw);
        height: min(280px, 40vw);
        background: var(--glow-fuchsia);
      }

      .actions {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-md);
      }

      .hero .actions {
        justify-content: flex-start;
      }

      .band--cta .actions {
        justify-content: center;
      }

      .cta-form {
        margin-top: var(--space-md);
      }

      @media (max-width: 639px) {
        .hero {
          margin-top: calc(-1 * var(--space-lg));
          padding: var(--space-2xl) var(--space-md);
        }

        .band,
        .section {
          padding: var(--space-2xl) var(--space-md);
        }

        .band--story .band-inner {
          padding-left: var(--space-lg);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .hero-orb,
        .band-orb {
          animation: none;
        }

        .reveal {
          opacity: 1;
          transform: none;
          transition: none;
        }
      }
    `,
  ];

  render() {
    return html`
      <section class="hero full-bleed" aria-label="Sobre mí">
        <span class="hero-orb hero-orb--1" aria-hidden="true"></span>
        <span class="hero-orb hero-orb--2" aria-hidden="true"></span>

        <div class="hero-inner">
          <div class="hero-copy">
            <span class="section-label">Sobre mí</span>
            <h1 class="hero-title">
              Alejandro Serrano <span class="title-grad">Notivoli</span>
            </h1>
            <p class="hero-tagline">Desarrollador · JavaScript, Cloudflare y automatización</p>
            <p class="hero-lead">
              Construyo herramientas digitales para problemas reales, de la idea al despliegue.
              Si estás aquí, no busques un catálogo. Busca a la persona que lo construye.
            </p>
            ${this.renderChannels()}
          </div>
          <figure class="hero-portrait">
            <img
              src="/profile_me_back_bg.png"
              alt="Retrato de Alejandro Serrano Notivoli"
              width="1086"
              height="1448"
              decoding="async"
              fetchpriority="high"
            />
          </figure>
        </div>
      </section>

      <section class="band band--story full-bleed" aria-label="Por qué trabajo así">
        <div class="band-inner story reveal">
          <span class="section-label">Historia</span>
          <h2 class="section-title">
            Por qué trabajo <span class="title-grad">así</span>
          </h2>
          <p>
            Me irrita el trabajo a medias. El folio en blanco, los acuerdos de palabra, las
            herramientas que prometen magia y entregan ruido. Por eso construyo productos
            completos: claros, usables y con reglas.
          </p>
          <p>
            Llevo años en frontend de alta criticidad y montando cosas propias en el Edge. Esa
            mezcla —sistemas que no pueden fallar y productos que tienen que servir a alguien de
            verdad— es lo que me ha enseñado a no vender atajos.
          </p>
          <p>
            Soy una persona. Si trabajamos juntos, hablas conmigo. Cuando algo se sale de lo que
            hago, tengo red de freelance de otros sectores —diseño, copy, lo que pida el
            trabajo— y lo encajo. Tú no desapareces en un organigrama. También construyo las
            mías: no como catálogo, como prueba de que lo que prometo lo he tenido que poner en
            producción.
          </p>
        </div>
      </section>

      <section class="section" aria-label="Cómo trabajo">
        <span class="section-label reveal">Cómo trabajo</span>
        <h2 class="section-title reveal reveal-delay-1">Cómo te acompaño</h2>
        <div class="open-grid">
          ${CONVICTIONS.map(
            (item, i) => html`
              <article class="open-item reveal ${i > 0 ? `reveal-delay-${i}` : ''}">
                <span class="item-label">${item.label}</span>
                <h3>${item.title}</h3>
                <p>${item.description}</p>
              </article>
            `,
          )}
        </div>
      </section>

      <section class="section" aria-label="Qué puedo construir">
        <span class="section-label reveal">Oficio</span>
        <h2 class="section-title reveal reveal-delay-1">
          Lo que puedo <span class="title-grad">construir</span>
        </h2>
        <p class="section-lead reveal reveal-delay-2">
          No hay una lista cerrada de herramientas. Eliges el problema; yo elijo las piezas —y,
          si hace falta, a alguien de otro oficio— para resolverlo.
        </p>
        <div class="open-grid">
          ${CAPABILITIES.map(
            (item, i) => html`
              <article class="open-item reveal ${i > 0 ? `reveal-delay-${Math.min(i, 4)}` : ''}">
                <h3>${item.title}</h3>
                <p>${item.description}</p>
              </article>
            `,
          )}
        </div>
      </section>

      <section class="band band--cta full-bleed" aria-label="Hablemos">
        <span class="band-orb band-orb--1" aria-hidden="true"></span>
        <div class="band-inner reveal">
          <span class="section-label">Hablemos</span>
          <h2 class="section-title">
            Si quieres, <span class="title-grad">hablemos</span>
          </h2>
          <p class="band-lead">
            Reserva una llamada. Si prefieres escribir, el email está aquí. LinkedIn y Substack
            también.
          </p>
          ${this.renderChannels()}
          <div class="cta-form">
            <ui-button variant="ghost" @ui-click=${() => this.goTo('/contacto')}
              >Escribir un mensaje</ui-button
            >
          </div>
        </div>
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'about-page': AboutPage;
  }
}
